import { useState, useEffect } from 'react';
import { Container, Box, Typography, Grid, Popover } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Header, AdPlaceholder, SEOTags, Footer } from '@components/shared';
import { SearchBar } from './SearchBar';
import { EmojiGrid } from './EmojiGrid';
import { SkinToneSelector } from './SkinToneSelector';
import emojiData from '@config/emoji.json';
import { EmojiData, SkinTone } from '@appTypes/emoji';
import { getStoredSkinTone, storeSkinTone } from '@utils/skinToneUtils';
import { trackEvent } from '@utils/analytics';
import { getPopularEmojis, recordEmojiCopy, clearPopularEmojis } from '@utils/popularEmojis';
import { PopularEmojis } from './PopularEmojis';
import { InstallBanner } from '@components/shared/InstallBanner';
import { useCopyEmoji } from '@/hooks/useCopyEmoji';

export const HomePage = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [skinTone, setSkinTone] = useState<SkinTone>('default');
  const [popularChars, setPopularChars] = useState<string[]>([]);

  // Shared copy interaction: clipboard write, emoji_copy analytics, popover
  // state, auto-close timer (cleared on re-copy and unmount).
  const {
    copy: handleEmojiClick,
    anchorEl,
    open: copiedOpen,
    close: handleSnackbarClose,
  } = useCopyEmoji({
    onCopied: (emoji) => {
      recordEmojiCopy(emoji);
      setPopularChars(getPopularEmojis());
    },
  });

  const data = emojiData as EmojiData;

  // Load preferences on mount
  useEffect(() => {
    setSkinTone(getStoredSkinTone());
    setPopularChars(getPopularEmojis());
  }, []);

  // Handle skin tone change
  const handleSkinToneChange = (newSkinTone: SkinTone) => {
    setSkinTone(newSkinTone);
    storeSkinTone(newSkinTone);
    trackEvent('skin_tone_change', { event_label: newSkinTone });
  };

  return (
    <Box className="app" component="main">
      <SEOTags title={t('home.seoTitle')} description={t('home.seoDescription')} />
      <Header />
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Typography
          variant="h1"
          component="h1"
          sx={{ fontSize: { xs: '1.4rem', sm: '1.75rem' }, fontWeight: 700, mb: 2, mt: 1 }}
        >
          {t('home.heading')}
        </Typography>

        <InstallBanner />

        <Grid container spacing={3}>
          <Grid item xs={12} lg={9}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <SearchBar value={searchQuery} onChange={setSearchQuery} />
              </Box>
              <SkinToneSelector
                skinToneOptions={data.skinToneOptions || []}
                selectedSkinTone={skinTone}
                onSkinToneChange={handleSkinToneChange}
              />
            </Box>
            {!searchQuery && (
              <PopularEmojis
                chars={popularChars}
                data={data}
                onEmojiClick={handleEmojiClick}
                onClear={() => {
                  clearPopularEmojis();
                  setPopularChars([]);
                }}
              />
            )}
            <Box sx={{ mt: 1 }} component="section" aria-label={t('a11y.emojiList')}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                {t('emojiVersion')}: {data.specVersion}
              </Typography>
              <EmojiGrid
                data={data}
                searchQuery={searchQuery}
                onEmojiClick={handleEmojiClick}
                skinTone={skinTone}
              />
            </Box>
          </Grid>
          <Grid item xs={12} lg={3}>
            <AdPlaceholder />
          </Grid>
        </Grid>
      </Container>

      {anchorEl && (
        <Popover
          open={copiedOpen}
          anchorEl={anchorEl}
          onClose={handleSnackbarClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          PaperProps={{
            sx: {
              bgcolor: 'success.main',
              color: 'white',
              px: 2,
              py: 0.75,
              borderRadius: 1.5,
              boxShadow: 3,
              mt: -1,
            },
          }}
          sx={{
            pointerEvents: 'none',
          }}
          disableScrollLock
          disableRestoreFocus
        >
          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
            {t('copied')}
          </Typography>
        </Popover>
      )}
      <Footer />
    </Box>
  );
};
